import { useMemo, useState, Children } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { BOARD_COLUMNS, COLUMN_DROP_STATUS, loadsForColumn } from '@logisticslash/shared';
import { ModuleHeader } from '@/components/ui/module-header';
import { LiveStatusStrip } from '@/components/ui/live-status-strip';
import { PageToolbar, FilterChip } from '@/components/ui/page-toolbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { DISPATCH } from '@/constants/testIds';
import { cn } from '@/lib/utils';

/** Visible ops columns (exclude done/closed clutter on main board). */
const COLUMNS = BOARD_COLUMNS.filter((c) =>
  ['pending', 'assigned', 'transit', 'delivery'].includes(c.key)
);

function isAtRisk(load) {
  if (!load.deliveryDate) return false;
  const due = new Date(load.deliveryDate).getTime();
  if (Number.isNaN(due)) return false;
  const hours = (due - Date.now()) / 3600000;
  return hours < 24 && !['delivered', 'closed', 'invoiced', 'canceled', 'cancelled'].includes(load.status);
}

function DropColumn({ column, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  return (
    <div
      ref={setNodeRef}
      data-testid={DISPATCH.column(column.key)}
      className={cn('flex flex-col', isOver && 'ring-2 ring-primary/40 rounded-lg')}
    >
      <div className="bg-muted/50 rounded-t-lg px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">
          {column.label}
          <span className="ml-2 text-muted-foreground">({Children.count(children)})</span>
        </h3>
      </div>
      <div className="flex-1 space-y-3 p-3 bg-muted/20 rounded-b-lg min-h-[400px]">{children}</div>
    </div>
  );
}

function LoadCard({ load, drivers, vehicles, onAssign }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: load.id,
  });
  const [driverId, setDriverId] = useState(load.driverId ?? '');
  const [vehicleId, setVehicleId] = useState(load.vehicleId ?? '');
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 }
    : undefined;

  const driverOpts = drivers.map((d) => ({ value: d.id, label: d.name }));
  const vehicleOpts = vehicles.map((v) => ({ value: v.id, label: v.unitNumber || v.id }));

  return (
    <Card
      ref={setNodeRef}
      style={style}
      data-testid={DISPATCH.card(load.id)}
      className={cn('cursor-move hover:shadow-lg', isAtRisk(load) && 'border-destructive/50')}
      {...listeners}
      {...attributes}
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-mono">{load.loadNumber || load.id}</CardTitle>
          <StatusPill status={load.status} />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {load.route || [load.shipperName, load.consigneeName].filter(Boolean).join(' → ') || '—'}
        </p>
      </CardHeader>
      <CardContent className="p-4 pt-2 space-y-2" onPointerDown={(e) => e.stopPropagation()}>
        {load.status === 'draft' || !load.driverId ? (
          <div className="space-y-2 pt-1">
            <Combobox
              options={driverOpts}
              value={driverId}
              onChange={setDriverId}
              placeholder="Assign driver"
            />
            <Combobox
              options={vehicleOpts}
              value={vehicleId}
              onChange={setVehicleId}
              placeholder="Assign vehicle"
            />
            {vehicles.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No vehicles in Fleet — add units in Fleet first.
              </p>
            ) : null}
            <Button
              size="sm"
              className="w-full"
              disabled={!driverId || !vehicleId}
              onClick={() => onAssign(load.id, driverId, vehicleId)}
            >
              Dispatch
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs pt-2 text-muted-foreground">
            <span>{drivers.find((d) => d.id === load.driverId)?.name || 'Assigned'}</span>
            <span>·</span>
            <span>
              {vehicles.find((v) => v.id === load.vehicleId)?.unitNumber || load.vehicleId || 'N/A'}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DispatchPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const boardQ = useQuery({
    queryKey: ['dispatch-board'],
    queryFn: async () => {
      const [loadsR, driversR, vehiclesR] = await Promise.all([
        api('/api/v1/loads'),
        api('/api/v1/drivers'),
        api('/api/v1/fleet/vehicles'),
      ]);
      if (loadsR.status !== 200) {
        throw new Error(String(loadsR.data?.error ?? 'Failed to load board'));
      }
      return {
        loads: loadsR.data.loads ?? [],
        drivers: driversR.status === 200 ? driversR.data.drivers ?? [] : [],
        vehicles: vehiclesR.status === 200 ? vehiclesR.data.vehicles ?? [] : [],
      };
    },
  });

  const loads = boardQ.data?.loads ?? [];
  const drivers = boardQ.data?.drivers ?? [];
  const vehicles = boardQ.data?.vehicles ?? [];

  const filtered = useMemo(() => {
    return loads.filter((l) => {
      if (filter === 'at-risk') return isAtRisk(l);
      if (filter === 'unassigned') return !l.driverId;
      return true;
    });
  }, [loads, filter]);

  const grouped = useMemo(() => {
    const map = {};
    for (const col of COLUMNS) {
      const rows = loadsForColumn(filtered, col.key).slice().sort((a, b) => {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return tb - ta;
      });
      map[col.key] = rows;
    }
    return map;
  }, [filtered]);

  const activeLoad = loads.find((l) => l.id === activeId);

  async function refresh() {
    await qc.invalidateQueries({ queryKey: ['dispatch-board'] });
  }

  function patchLoadInCache(loadId, patch) {
    qc.setQueryData(['dispatch-board'], (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        loads: prev.loads.map((l) => (l.id === loadId ? { ...l, ...patch } : l)),
      };
    });
  }

  async function move(loadId, columnKey) {
    const nextStatus = COLUMN_DROP_STATUS[columnKey];
    if (!nextStatus) {
      toast.error('Invalid board column');
      return;
    }

    const load = loads.find((l) => l.id === loadId);
    const needsAssign = nextStatus === 'dispatched' || nextStatus === 'in_transit';
    if (needsAssign && (!load?.driverId || !load?.vehicleId)) {
      toast.error('Assign driver and vehicle before moving to Assigned or In Transit');
      return;
    }

    // Optimistic column move — never blank the board on failure.
    const prevStatus = load?.status;
    patchLoadInCache(loadId, { status: nextStatus, updatedAt: new Date().toISOString() });

    const r = await api('/api/v1/dispatch/assign', {
      method: 'POST',
      body: { loadId, columnKey },
    });
    if (r.status === 200 && r.data?.load?.status) {
      toast.success('Board updated');
      patchLoadInCache(loadId, r.data.load);
    } else {
      toast.error(String(r.data?.error ?? 'Move failed'));
      if (prevStatus) patchLoadInCache(loadId, { status: prevStatus });
    }
  }

  async function assign(loadId, driverId, vehicleId) {
    if (!driverId || !vehicleId) {
      toast.error('Driver and vehicle are both required');
      return;
    }
    const r = await api(`/api/v1/loads/${loadId}/assign`, {
      method: 'POST',
      body: { driverId, vehicleId },
    });
    if (r.status === 200) {
      toast.success('Load dispatched');
      if (r.data?.load) {
        patchLoadInCache(loadId, r.data.load);
      } else {
        patchLoadInCache(loadId, {
          driverId,
          vehicleId,
          status: 'dispatched',
          updatedAt: new Date().toISOString(),
        });
      }
      void refresh();
    } else {
      toast.error(String(r.data?.error ?? 'Assign failed'));
    }
  }

  function onDragEnd(e) {
    setActiveId(null);
    const loadId = String(e.active.id);
    const overId = e.over?.id ? String(e.over.id) : null;
    if (!overId) return;

    let targetKey = COLUMNS.find((c) => c.key === overId)?.key;
    if (!targetKey) {
      // Dropped on another card — resolve that card's column
      for (const col of COLUMNS) {
        if ((grouped[col.key] || []).some((l) => l.id === overId)) {
          targetKey = col.key;
          break;
        }
      }
    }
    if (!targetKey) return;

    const fromKey = COLUMNS.find((c) => (grouped[c.key] || []).some((l) => l.id === loadId))?.key;
    if (fromKey === targetKey) return;
    void move(loadId, targetKey);
  }

  return (
    <div className="space-y-6 motion-enter">
      <ModuleHeader
        title="Dispatch Board"
        description="Drag cards across columns or assign driver and vehicle"
        icon="Columns3"
      />

      <LiveStatusStrip
        metrics={[
          { label: 'Pending', value: String(grouped.pending?.length || 0) },
          { label: 'Assigned', value: String(grouped.assigned?.length || 0) },
          {
            label: 'In Transit',
            value: String(grouped.transit?.length || 0),
            className: 'text-primary',
          },
          { label: 'At risk', value: String(loads.filter(isAtRisk).length) },
        ]}
      />

      <PageToolbar>
        {[
          { id: 'all', label: 'All' },
          { id: 'unassigned', label: 'Unassigned' },
          { id: 'at-risk', label: 'At risk' },
        ].map((f) => (
          <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label}
          </FilterChip>
        ))}
      </PageToolbar>

      {boardQ.isLoading && !boardQ.data ? (
        <p className="text-muted-foreground text-sm">Loading board…</p>
      ) : boardQ.isError && !boardQ.data ? (
        <p className="text-destructive text-sm">Could not load dispatch board</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={(e) => setActiveId(String(e.active.id))}
          onDragEnd={onDragEnd}
        >
          <div
            data-testid={DISPATCH.board}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {COLUMNS.map((column) => (
              <DropColumn key={column.key} column={column}>
                {(grouped[column.key] || []).map((load) => (
                  <LoadCard
                    key={load.id}
                    load={load}
                    drivers={drivers}
                    vehicles={vehicles}
                    onAssign={assign}
                  />
                ))}
              </DropColumn>
            ))}
          </div>
          <DragOverlay>
            {activeLoad ? (
              <Card className="p-4 shadow-xl">
                <p className="font-mono text-sm">{activeLoad.loadNumber || activeLoad.id}</p>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
